import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    IconAbc,
    IconAlertCircle,
    IconLetterCase,
    IconNumbers,
    IconTag,
} from '@tabler/icons-react';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import Hint from '../../../../../components/common/Hint';
import OutputFormatSelect from '../shared/OutputFormatSelect';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import ToolCard from '../../components/common/ToolCard';
import ToolReadyHint from '../../components/common/ToolReadyHint';
import BarcodePreview from './BarcodePreview';
import {
    BARCODE_FORMATS,
    BARCODE_OUTPUT_FORMATS,
    exportBarcode,
    HEIGHT_OPTIONS,
    generateBarcodePreview,
    normaliseBarcodeValue,
    outputFileName,
    validateBarcodeValue,
} from './barcodeImageEngine';
import {
    deriveToolStatus,
    downloadBlob,
    getToolStatusText,
} from '../../toolPageUtils';

/** Glyph-over-label tile, the tray shape the other image tools use. */
function tileBtnClass(active) {
    return `flex min-h-[54px] flex-col items-center justify-center gap-1 rounded-lg border-none px-1 py-1.5 text-[11px] leading-tight transition-all ${
        active
            ? 'bg-white font-semibold text-amber-600 shadow-[0_1px_2px_rgba(28,24,21,0.04)] dark:bg-zinc-900 dark:text-amber-400'
            : 'bg-transparent font-medium text-[#6B635C] dark:text-zinc-400'
    }`;
}

// Each icon says what the symbology eats: any text, uppercase alphanumerics,
// a fixed run of digits, a retail product number.
const FORMAT_ICONS = {
    CODE128: IconAbc,
    CODE39: IconLetterCase,
    EAN13: IconNumbers,
    UPC: IconTag,
};


/** Bar height, drawn as bars of that height so the choice is visible. */
function HeightGlyph({ px, active }) {
    const color = active ? '#F59E0B' : '#9A9AA3';
    const h = Math.round((px / 150) * 18) + 4;
    return (
        <span className="flex h-[22px] w-[22px] items-end justify-center gap-[2px]">
            {[0.75, 1, 0.6, 1].map((k, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <span key={i} style={{ background: color, width: 2, height: Math.max(4, h * k), borderRadius: 1 }} />
            ))}
        </span>
    );
}

const inputClass = 'w-full rounded-lg border border-[#ECE8E4] bg-white px-3 py-2 text-[13px] text-[#3D3833] outline-none transition-colors placeholder:text-[#A8A099] focus:border-amber-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 dark:placeholder:text-zinc-500';

const PLACEHOLDER_KEYS = {
    CODE128: 'barcodeImage.placeholders.code128',
    CODE39: 'barcodeImage.placeholders.code39',
    EAN13: 'barcodeImage.placeholders.ean13',
    UPC: 'barcodeImage.placeholders.upc',
};

export default function BarcodeImage() {
    const { t } = useTranslation();

    const [barcodeFormat, setBarcodeFormat] = useState('CODE128');
    const [value, setValue] = useState('');
    const [lineColor, setLineColor] = useState('#000000');
    const [background, setBackground] = useState('#ffffff');
    const [height, setHeight] = useState(100);
    const [barWidth, setBarWidth] = useState(2);
    const [margin, setMargin] = useState(10);
    const [displayValue, setDisplayValue] = useState(true);
    const [downloadFormat, setDownloadFormat] = useState('png');
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const previewUrlRef = useRef(null);

    const revokePreviewUrl = useCallback(() => {
        if (previewUrlRef.current?.startsWith('blob:')) {
            URL.revokeObjectURL(previewUrlRef.current);
        }
        previewUrlRef.current = null;
    }, []);

    useEffect(() => () => revokePreviewUrl(), [revokePreviewUrl]);

    const validation = useMemo(
        () => validateBarcodeValue(barcodeFormat, value),
        [barcodeFormat, value],
    );
    const canGenerate = validation.ok;

    // Everything except "nothing typed yet" is worth saying out loud — an empty
    // preview with no reason is what makes EAN-13 and UPC feel broken.
    const validationMessage = validation.ok || validation.reason === 'empty'
        ? null
        : validation.reason === 'checksum'
            ? t('barcodeImage.invalid.checksum', { digit: toBnDigits(validation.expected) })
            : t(`barcodeImage.invalid.${validation.reason}.${barcodeFormat}`);

    const barcodeOptions = useMemo(() => ({
        format: barcodeFormat,
        lineColor,
        background,
        height,
        barWidth,
        margin,
        displayValue,
        downloadFormat,
    }), [barcodeFormat, lineColor, background, height, barWidth, margin, displayValue, downloadFormat]);

    useEffect(() => {
        const text = normaliseBarcodeValue(barcodeFormat, value).trim();
        if (!canGenerate) {
            revokePreviewUrl();
            setResult(null);
            setError(null);
            return;
        }

        try {
            const generated = generateBarcodePreview(text, barcodeOptions);
            revokePreviewUrl();
            if (generated.previewUrl.startsWith('blob:')) {
                previewUrlRef.current = generated.previewUrl;
            }
            setResult(generated);
            setError(null);
        } catch {
            revokePreviewUrl();
            setResult(null);
            setError(t('barcodeImage.errors.failed'));
        }
    }, [barcodeFormat, barcodeOptions, canGenerate, revokePreviewUrl, t, value]);

    const handleDownload = async () => {
        if (!result) return;
        recordToolUsage('image/barcode', { category: 'image' });
        try {
            // `ext` is what the file REALLY is: a browser that cannot write the
            // chosen format falls back inside the encoder.
            const out = await exportBarcode(result, downloadFormat);
            downloadBlob(out.blob, outputFileName(out.ext));
        } catch {
            setError(t('barcodeImage.errors.failed'));
        }
    };

    const handleReset = () => {
        revokePreviewUrl();
        setBarcodeFormat('CODE128');
        setValue('');
        setLineColor('#000000');
        setBackground('#ffffff');
        setHeight(100);
        setBarWidth(2);
        setMargin(10);
        setDisplayValue(true);
        setDownloadFormat('png');
        setResult(null);
        setError(null);
    };

    const status = deriveToolStatus({
        hasInput: value.trim().length > 0,
        canRun: canGenerate,
        hasResult: !!result,
        hasError: !!error,
    });

    const statusText = getToolStatusText(status, t, {
        ready: t('toolPage.statusText.ready'),
        success: t('barcodeImage.done'),
    });

    return (
        <ToolPageLayout
            slug="image/barcode"
            subtitleKey="barcodeImage.subtitle"
            status={status}
            statusText={statusText}
            controlsTitle={t('barcodeImage.contentTitle')}
            // The preview column tracks the settings column's height, so the
            // panel ends where the settings do instead of leaving a gap.
            matchSidebarHeight
            showTrust={false}
            onReset={value || result || error ? handleReset : undefined}
            primaryAction={{
                label: t('toolPage.downloadAs', { format: t(`toolPage.outputFormat.formats.${downloadFormat}`) }),
                onClick: handleDownload,
                disabled: !result,
            }}
            actionExtra={(
                <OutputFormatSelect
                    formats={BARCODE_OUTPUT_FORMATS}
                    value={downloadFormat}
                    onChange={setDownloadFormat}
                />
            )}
            controls={(
                <div className="space-y-4">
                    <ToolCard title={t('barcodeImage.formatTitle')}>
                        <div className="grid grid-cols-4 gap-1 rounded-xl bg-[#F7F5F3] p-1 dark:bg-zinc-800">
                            {BARCODE_FORMATS.map((id) => {
                                const Icon = FORMAT_ICONS[id] || IconNumbers;
                                return (
                                    <Hint label={t(`barcodeImage.hints.${id}`)} key={id}>
                                        <button
                                            type="button"
                                            onClick={() => setBarcodeFormat(id)}
                                            className={tileBtnClass(barcodeFormat === id)}
                                        >
                                            <Icon size={20} stroke={1.8} />
                                            {t(`barcodeImage.formats.${id}`)}
                                        </button>
                                    </Hint>
                                );
                            })}
                        </div>
                    </ToolCard>

                    <ToolCard title={t('barcodeImage.valueTitle')}>
                        <input
                            type="text"
                            value={value}
                            // Numeric symbologies keep only digits, so a value
                            // pasted with the spaces printed on the packet works.
                            onChange={(e) => setValue(normaliseBarcodeValue(barcodeFormat, e.target.value))}
                            placeholder={t(PLACEHOLDER_KEYS[barcodeFormat])}
                            className={`${inputClass} ${validationMessage ? 'border-red-300 focus:border-red-400 dark:border-red-500/50' : ''}`}
                        />
                        {validationMessage ? (
                            <p className="mt-2 flex items-start gap-1.5 text-[11.5px] font-medium text-red-600 dark:text-red-400">
                                <IconAlertCircle size={14} stroke={2} className="mt-px flex-none" />
                                {validationMessage}
                            </p>
                        ) : (
                            <p className="mt-2 text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                                {t(`barcodeImage.hints.${barcodeFormat}`)}
                            </p>
                        )}
                    </ToolCard>
                </div>
            )}
            settings={(
                <div className="space-y-3.5">
                    <ToolCard title={t('barcodeImage.designTitle')}>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <label className="block space-y-1.5">
                                <span className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                    {t('barcodeImage.lineColor')}
                                </span>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="color"
                                        value={lineColor}
                                        onChange={(e) => setLineColor(e.target.value)}
                                        className="h-10 w-12 cursor-pointer rounded-lg border border-[#ECE8E4] bg-white p-1 dark:border-zinc-700 dark:bg-zinc-900"
                                    />
                                    <input
                                        type="text"
                                        value={lineColor}
                                        onChange={(e) => setLineColor(e.target.value)}
                                        className={`${inputClass} font-mono text-[12px]`}
                                    />
                                </div>
                            </label>
                            <label className="block space-y-1.5">
                                <span className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                    {t('barcodeImage.background')}
                                </span>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="color"
                                        value={background}
                                        onChange={(e) => setBackground(e.target.value)}
                                        className="h-10 w-12 cursor-pointer rounded-lg border border-[#ECE8E4] bg-white p-1 dark:border-zinc-700 dark:bg-zinc-900"
                                    />
                                    <input
                                        type="text"
                                        value={background}
                                        onChange={(e) => setBackground(e.target.value)}
                                        className={`${inputClass} font-mono text-[12px]`}
                                    />
                                </div>
                            </label>
                        </div>

                        <label className="mt-3 block space-y-1.5">
                            <span className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                {t('barcodeImage.heightTitle')}
                            </span>
                            <div className="grid grid-cols-5 gap-1 rounded-xl bg-[#F7F5F3] p-1 dark:bg-zinc-800">
                                {/* Glyphs only — each one grows with its height, and the
                                    exact size is in the tooltip / aria-label. */}
                                {HEIGHT_OPTIONS.map((px) => (
                                    <Hint label={`${toBnDigits(px)}px`} key={px}>
                                        <button
                                            type="button"
                                            onClick={() => setHeight(px)}
                                            aria-label={`${toBnDigits(px)}px`}
                                            aria-pressed={height === px}
                                            className={tileBtnClass(height === px)}
                                        >
                                            <HeightGlyph px={px} active={height === px} />
                                        </button>
                                    </Hint>
                                ))}
                            </div>
                        </label>

                        <label htmlFor="barcode-bar-width" className="mt-3 block space-y-1.5">
                            <span className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                {t('barcodeImage.barWidthLabel', { value: toBnDigits(barWidth) })}
                            </span>
                            <input
                                id="barcode-bar-width"
                                type="range"
                                min={1}
                                max={4}
                                value={barWidth}
                                onChange={(e) => setBarWidth(Number(e.target.value))}
                                className="w-full accent-amber-500"
                            />
                        </label>

                        <label htmlFor="barcode-margin" className="mt-3 block space-y-1.5">
                            <span className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                {t('barcodeImage.marginLabel', { value: toBnDigits(margin) })}
                            </span>
                            <input
                                id="barcode-margin"
                                type="range"
                                min={0}
                                max={40}
                                value={margin}
                                onChange={(e) => setMargin(Number(e.target.value))}
                                className="w-full accent-amber-500"
                            />
                        </label>

                        <label className="mt-3 flex items-center gap-2 text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                            <input
                                type="checkbox"
                                checked={displayValue}
                                onChange={(e) => setDisplayValue(e.target.checked)}
                                className="h-4 w-4 rounded border-[#ECE8E4] text-amber-600 focus:ring-amber-500 dark:border-zinc-700"
                            />
                            {t('barcodeImage.showText')}
                        </label>
                    </ToolCard>
                </div>
            )}
            preview={(
                <ToolPreviewPanel
                    title={t('barcodeImage.previewTitle')}
                    fill
                    tall={false}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('barcodeImage.previewEmpty')}
                    idleHint={t('barcodeImage.previewEmptyHint')}
                    processingLabel={t('barcodeImage.generating')}
                    error={error}
                    successLabel={t('barcodeImage.done')}
                    readyHint={status === 'ready' && !result ? <ToolReadyHint actionLabel={t('barcodeImage.valueTitle')} /> : null}
                    showIdle={!result && !error}
                >
                    {result ? (
                        <BarcodePreview
                            previewUrl={result.previewUrl}
                            alt={t('barcodeImage.previewAlt')}
                            format={downloadFormat}
                        />
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
    );
}
